<script>
import api from "@/helpers/api-rest/api.js";
import format from "date-fns/format";
import parseISO from "date-fns/parseISO";
import statusHelper from "@/helpers/general/status.js";

// Cohesive palette (purple / indigo / blue / teal / coral) — colors cycle by status id
const STATUS_COLOR_PALETTE = [
  "#5b73e8",
  "#564ab1",
  "#5438DC",
  "#34c38f",
  "#50a5f1",
  "#f1b44c",
  "#f46a6a",
  "#74788d",
  "#299cdb",
  "#41508c",
];

/**
 * Dashboard widget: essay distribution by status + paginated table (server-side).
 */
export default {
  data() {
    return {
      items: [],
      isLoading: false,
      loadError: null,
      selectedStatusId: null,
      tableRows: [],
      tableLoading: false,
      tablePage: 1,
      tablePerPage: 10,
      tablePageOptions: [10, 25, 50],
      tableTotalRows: 0,
      statusHelper,
      tableFields: [
        {
          key: "name",
          sortable: false,
          label: "Nombre",
          tdClass: "essays-dash-col-name",
        },
        {
          key: "project_name",
          sortable: false,
          label: "Convocatoria",
          tdClass: "essays-dash-col-project",
        },
        { key: "creator_name", sortable: false, label: "Usuario" },
        {
          key: "created_at",
          sortable: false,
          label: "Fecha de creación",
        },
        { key: "essay_status_name", sortable: false, label: "Estado" },
        { key: "type", sortable: false, label: "Tipo" },
        { key: "current_step", sortable: false, label: "Redacción" },
        {
          key: "action",
          tdClass: "text-nowrap",
          sortable: false,
          label: "",
        },
      ],
    };
  },
  computed: {
    series() {
      return this.items.map((row) => row.count);
    },
    labels() {
      return this.items.map((row) => row.name || "—");
    },
    chartColors() {
      return this.items.map((row) => this.colorForStatusId(row.id));
    },
    hasChartData() {
      return this.items.length > 0 && this.series.some((n) => n > 0);
    },
    chartOptions() {
      const vm = this;
      return {
        chart: {
          type: "donut",
          height: 200,
          toolbar: { show: false },
          events: {
            dataPointSelection(_event, _ctx, config) {
              const idx = config.dataPointIndex;
              const row = vm.items[idx];
              if (row) {
                vm.selectStatus(row.id);
              }
            },
          },
        },
        labels: this.labels,
        colors: this.chartColors,
        legend: { show: false },
        dataLabels: {
          enabled: true,
          formatter(val) {
            return `${Math.round(val)}%`;
          },
        },
        plotOptions: {
          pie: {
            donut: {
              size: "72%",
              labels: {
                show: true,
                name: { show: true },
                value: {
                  show: true,
                  formatter(val) {
                    return val;
                  },
                },
                total: {
                  show: true,
                  label: "Total",
                  formatter: () => this.totalCount,
                },
              },
            },
          },
        },
        stroke: { show: false },
      };
    },
    totalCount() {
      return this.items.reduce((sum, row) => sum + row.count, 0);
    },
    leftColumnItems() {
      const mid = Math.ceil(this.items.length / 2);
      return this.items.slice(0, mid);
    },
    rightColumnItems() {
      const mid = Math.ceil(this.items.length / 2);
      return this.items.slice(mid);
    },
  },
  mounted() {
    this.loadDistribution();
    this.loadTable();
  },
  methods: {
    colorForStatusId(id) {
      const idx = Math.abs(Number(id)) % STATUS_COLOR_PALETTE.length;
      return STATUS_COLOR_PALETTE[idx];
    },
    formatDate(iso) {
      if (!iso) {
        return "—";
      }
      try {
        return format(parseISO(iso), "dd/MM/yyyy");
      } catch {
        return "—";
      }
    },
    selectStatus(statusId) {
      this.selectedStatusId = statusId;
      this.tablePage = 1;
      this.loadTable();
    },
    clearStatusFilter() {
      this.selectedStatusId = null;
      this.tablePage = 1;
      this.loadTable();
    },
    async loadDistribution() {
      this.isLoading = true;
      this.loadError = null;
      try {
        const response = await api.get({
          url: "/api/v1/dashboard/essays-by-status",
          withLoading: false,
        });
        const raw = response?.data?.data?.items;
        this.items = Array.isArray(raw) ? raw : [];
      } catch (error) {
        console.error("Error loading essays by status:", error);
        this.loadError = error;
        this.items = [];
      } finally {
        this.isLoading = false;
      }
    },
    async loadTable() {
      this.tableLoading = true;
      try {
        const params = {
          page: this.tablePage,
          per_page: this.tablePerPage,
        };
        if (this.selectedStatusId != null) {
          params.essay_status_id = this.selectedStatusId;
        }
        const response = await api.get({
          url: "/api/v1/dashboard/essays-list",
          params,
          withLoading: false,
        });
        const rows = response?.data?.data;
        const meta = response?.data?.meta;
        this.tableRows = Array.isArray(rows) ? rows : [];
        this.tableTotalRows = meta?.total != null ? Number(meta.total) : 0;
      } catch (error) {
        console.error("Error loading dashboard essays table:", error);
        this.tableRows = [];
        this.tableTotalRows = 0;
      } finally {
        this.tableLoading = false;
      }
    },
    onTablePageChange() {
      this.loadTable();
    },
    onTablePerPageChange() {
      this.tablePage = 1;
      this.loadTable();
    },
    canViewEssay(row) {
      return Boolean(row.project_id);
    },
    essayTooltipConfig(text) {
      const t = text != null && String(text).length ? String(text) : "—";
      return {
        title: t,
        container: "body",
        boundary: "viewport",
      };
    },
  },
};
</script>

<template>
  <div class="card">
    <div class="card-body">
      <h4 class="card-title mb-3">Proyectos por estado</h4>

      <div v-if="isLoading" class="text-center py-4 text-muted">
        Cargando…
      </div>
      <div
        v-else-if="loadError"
        class="alert alert-warning mb-0"
        role="alert"
      >
        No se pudo cargar la distribución por estado.
      </div>
      <template v-else-if="items.length === 0">
        <p class="text-muted text-center mb-0">
          No hay estados configurados.
        </p>
      </template>
      <template v-else>
        <div v-if="hasChartData" class="mb-2">
          <apexchart
            type="donut"
            height="200"
            :options="chartOptions"
            :series="series"
          />
        </div>
        <p
          v-else
          class="text-muted text-center py-3 mb-0"
        >
          No hay proyectos registrados.
        </p>

        <div class="row justify-content-center">
          <div class="col-xl-5 col-md-6">
            <div class="mt-1">
              <div
                v-for="row in leftColumnItems"
                :key="'l-' + row.id"
                class="clearfix py-1 d-flex align-items-center status-legend-row"
                :class="{
                  'status-legend-row--active': selectedStatusId === row.id,
                }"
                role="button"
                tabindex="0"
                @click="selectStatus(row.id)"
                @keydown.enter.prevent="selectStatus(row.id)"
              >
                <span
                  class="d-inline-block rounded mr-2 flex-shrink-0"
                  style="width: 8px; height: 8px"
                  :style="{ backgroundColor: colorForStatusId(row.id) }"
                />
                <p class="text-muted mb-0 text-truncate flex-grow-1">
                  {{ row.name }}
                </p>
                <h5 class="font-size-16 m-0 ml-2 flex-shrink-0">
                  {{ row.count }}
                </h5>
              </div>
            </div>
          </div>
          <div class="col-xl-5 offset-xl-1 col-md-6">
            <div class="mt-1">
              <div
                v-for="row in rightColumnItems"
                :key="'r-' + row.id"
                class="clearfix py-1 d-flex align-items-center status-legend-row"
                :class="{
                  'status-legend-row--active': selectedStatusId === row.id,
                }"
                role="button"
                tabindex="0"
                @click="selectStatus(row.id)"
                @keydown.enter.prevent="selectStatus(row.id)"
              >
                <span
                  class="d-inline-block rounded mr-2 flex-shrink-0"
                  style="width: 8px; height: 8px"
                  :style="{ backgroundColor: colorForStatusId(row.id) }"
                />
                <p class="text-muted mb-0 text-truncate flex-grow-1">
                  {{ row.name }}
                </p>
                <h5 class="font-size-16 m-0 ml-2 flex-shrink-0">
                  {{ row.count }}
                </h5>
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="selectedStatusId != null"
          class="d-flex justify-content-between align-items-center flex-wrap mt-2 mb-2"
        >
          <small class="text-muted">Filtrado por estado seleccionado.</small>
          <button
            type="button"
            class="btn btn-link btn-sm p-0"
            @click="clearStatusFilter"
          >
            Ver todos
          </button>
        </div>

        <div class="mt-3 border-top pt-3">
          <div class="row align-items-center mb-2">
            <div class="col-sm-6">
              <label class="d-inline-flex align-items-center mb-0 small">
                Mostrar
                <b-form-select
                  v-model="tablePerPage"
                  size="sm"
                  class="mx-2"
                  :options="tablePageOptions"
                  @change="onTablePerPageChange"
                />
                registros
              </label>
            </div>
          </div>
          <div class="table-responsive mb-2">
            <b-table
              :items="tableRows"
              :fields="tableFields"
              :busy="tableLoading"
              show-empty
              empty-text="No hay proyectos."
              responsive="sm"
              class="mb-0"
              table-class="essays-by-status-table"
            >
              <template #cell(name)="{ item }">
                <span
                  class="essays-dash-clip"
                  v-b-tooltip.hover="essayTooltipConfig(item.name)"
                >{{ item.name || "—" }}</span>
              </template>
              <template #cell(project_name)="{ item }">
                <span
                  class="essays-dash-clip"
                  v-b-tooltip.hover="essayTooltipConfig(item.project_name)"
                >{{ item.project_name || "—" }}</span>
              </template>
              <template #cell(created_at)="{ item }">
                {{ formatDate(item.created_at) }}
              </template>
              <template #cell(essay_status_name)="{ item }">
                <b-badge
                  pill
                  :variant="
                    item.essay_status_id === 3
                      ? 'warning'
                      : item.essay_status_id === 4
                      ? 'danger'
                      : 'info'
                  "
                >
                  {{ item.essay_status_name || "—" }}
                </b-badge>
              </template>
              <template #cell(type)="{ item }">
                <b-badge
                  v-if="item.type"
                  pill
                  :variant="item.type === 'new' ? 'primary' : 'info'"
                >
                  {{
                    statusHelper.getStatus({
                      itemStatus: item.type,
                      type: "essayType",
                    })
                  }}
                </b-badge>
                <span v-else class="text-muted">—</span>
              </template>
              <template #cell(current_step)="{ item }">
                <b-badge
                  pill
                  :variant="item.current_step === 3 ? 'success' : 'warning'"
                >
                  {{
                    item.current_step === 3
                      ? "Finalizado"
                      : "Incompleto (Paso " +
                        (Number(item.current_step) + 1) +
                        ")"
                  }}
                </b-badge>
              </template>
              <template #cell(action)="{ item }">
                <router-link
                  v-if="canViewEssay(item)"
                  v-b-tooltip.hover
                  title="Ver"
                  class="text-secondary"
                  :to="{
                    name: 'essay-detail',
                    params: {
                      id: String(item.project_id),
                      essayId: String(item.id),
                    },
                  }"
                >
                  <i class="mdi mdi-eye-outline font-size-18"></i>
                </router-link>
                <span v-else class="text-muted small">—</span>
              </template>
            </b-table>
          </div>
          <div class="d-flex justify-content-end">
            <b-pagination
              v-model="tablePage"
              :total-rows="tableTotalRows"
              :per-page="tablePerPage"
              size="sm"
              class="mb-0"
              @input="onTablePageChange"
            />
          </div>
        </div>

        <div class="text-center mt-3">
          <router-link
            :to="{ name: 'Project' }"
            class="btn btn-primary btn-sm"
          >
            Ver convocatorias
          </router-link>
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped>
/* Single-line ellipsis + tooltip for essay / project columns */
::v-deep .essays-by-status-table {
  table-layout: fixed;
}

::v-deep .essays-dash-col-name {
  max-width: 200px;
  width: 22%;
  vertical-align: middle;
}

::v-deep .essays-dash-col-project {
  max-width: 260px;
  width: 28%;
  vertical-align: middle;
}

.essays-dash-clip {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
}

.status-legend-row {
  cursor: pointer;
  border-radius: 4px;
  padding-left: 4px;
  margin-left: -4px;
  transition: background-color 0.15s ease;
}

.status-legend-row:hover {
  background-color: rgba(85, 110, 230, 0.08);
}

.status-legend-row--active {
  background-color: rgba(85, 110, 230, 0.12);
  font-weight: 600;
}
</style>
